import { Box, Button, Stack } from '@chakra-ui/react'
import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { noOp } from '@/utils/helpers'

import ShwLink from '@/components/ShwLink'

import type { TabLabel, TabsRoundedProps } from './types'

export default function TabsRounded<T extends TabLabel>({
  tabs,
  currentTab,
  includeCheckmarks = false,
  onTabChange = noOp,
  tabToUrl,
  tabToLabel,
  separated = false,
  buttonProps = {},
  activeTextColor = 'primary.solid',
  defaultTextColor = 'fg.muted',
  ...props
}: TabsRoundedProps<T>): React.ReactElement {
  return (
    <Stack
      direction="row"
      // Chakra version currently has a bug with supporting negative spacing on Stack
      spacing={separated ? '8px' : '-0px'}
      flexWrap="wrap"
      rowGap="4px"
      maxWidth="100%"
      overflowX="scroll"
      css={{
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}
      {...props}
    >
      {tabs.map((tab, index) => {
        const isCurrentTab =
          tab === currentTab || (Array.isArray(currentTab) && currentTab.includes(tab))
        const maybeLabel: string | (() => React.ReactElement) | undefined = tabToLabel?.[tab]
        const label = typeof maybeLabel === 'function' ? maybeLabel() : maybeLabel
        const tabComponent = (
          <Button
            py="8px"
            px="16px"
            key={tab}
            variant="outline"
            onClick={() => {
              onTabChange(tab)
            }}
            color={isCurrentTab ? activeTextColor : defaultTextColor}
            backgroundColor={isCurrentTab ? 'primary.subtle' : 'bg'}
            borderRightRadius={index === tabs.length - 1 || separated ? '8px' : '0px'}
            borderLeftRadius={index === 0 || separated ? '8px' : '0px'}
            border="2px solid"
            borderColor={isCurrentTab ? 'primary.solid' : 'border.emphasized'}
            zIndex={isCurrentTab ? 1 : 0}
            whiteSpace="nowrap"
            {...buttonProps}
            ml={index === 0 || separated ? '0px' : '-2px'}
          >
            <Stack direction="row" spacing={2} align="center">
              {includeCheckmarks && isCurrentTab ? (
                <Box>
                  <FontAwesomeIcon icon={faCheck} />
                </Box>
              ) : null}
              <Box>{label ?? tab}</Box>
            </Stack>
          </Button>
        )
        return tabToUrl?.(tab) == null ? (
          tabComponent
        ) : (
          <ShwLink key={tabToUrl(tab)} noScroll href={tabToUrl(tab)}>
            {tabComponent}
          </ShwLink>
        )
      })}
    </Stack>
  )
}
